import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { isMergedChannel } from '@/lib/utils';

const initialState = {
  _id: null,
  ig_username: null,
  phone: null,
  email: null,
  channel: null,
  fullname: '',
  status: null,
  profile_picture_url: null,
  load_fb_past_messages: null,
  tags: [],
  owner: null,
  note: null,
  plan: null,
  ai_notes: null,
  followers_count: null,
  following_count: null,
  verified: false,
  bio: '',
  external_url: null,
  linkedin_profile_link: null,
  unipile_contact_id: null,

  page: 0,
  messages: [],
  responseType: null, // 'email', 'sms-email' (merged), or null - indicates if messages contain threads/mixed content

  commentThread: [],
  parentCommentId: null,
  commentThreadAccordianState: '',
  commentMessage: '',

  selectedPrivateReplyMessage: '',
  selectedPrivateReplyMessageId: null,

  selectedEmailThreadForReply: null, // { thread_id, subject, preview }

  // Reply to message state (for Instagram message replies)
  selectedReplyToMessage: null, // { _id, text, message_type, media_url }

  latest_message_received_timestamp: null,
  latest_comment_received_timestamp: null,

  rocket_selling_index: null,

  copilotMessages: [],
  messageToTweak: '',
  isCopilotOpen: false,
  isCopilotLoading: false,
  isShowingMobileProfile: false,
};

const useContactStore = create(
  devtools(
    (set) => ({
      ...initialState,

      setContactDetails: (contactData) =>
        set(
          {
            _id: contactData._id,
            ig_username: contactData.ig_username,
            fullname: contactData.full_name,
            phone: contactData.phone || null,
            email: contactData.email || null,
            status: contactData.status,
            profile_picture_url: contactData.profile_picture_url,
            load_fb_past_messages: contactData.load_fb_past_messages,
            tags: contactData.tags,
            owner: contactData.user_id,
            note: contactData.note,
            ai_notes: contactData.ai_notes,
            plan: contactData.plan,
            channel: contactData.channel,
            followers_count: contactData.followers_count,
            following_count: contactData.following_count,
            verified: contactData.verified,
            bio: contactData.bio,
            external_url: contactData.external_url,
            linkedin_profile_link: contactData.linkedin_profile_link,
            unipile_contact_id: contactData.unipile_contact_id,
            rocket_selling_index: contactData.rocket_selling_index,
            copilotMessages: contactData.copilot_conversations
              ? contactData.copilot_conversations
              : [],
            ...(contactData.latest_message_received_timestamp && {
              latest_message_received_timestamp:
                contactData.latest_message_received_timestamp,
            }),
            ...(contactData.latest_comment_received_timestamp && {
              latest_comment_received_timestamp:
                contactData.latest_comment_received_timestamp,
            }),
          },
          false,
          'setContactDetails'
        ),
      changeContact: (contactId) =>
        set({ _id: contactId }, false, 'changeContact'),
      setContactStatus: (status) =>
        set(
          {
            status,
          },
          false,
          'setContactStatus'
        ),
      setContactNote: (note) => set({ note }, false, 'setContactNote'),
      setContactOwner: (ownerId) =>
        set({ owner: ownerId }, false, 'setContactOwner'),
      setPlan: (plan) => set({ plan }, false, 'setPlan'),
      addContactTag: (newTag) =>
        set(
          (state) => ({ tags: [...state.tags, newTag] }),
          false,
          'addContactTag'
        ),
      removeContactTag: (tagId) =>
        set(
          (state) => ({
            tags: state.tags.filter((tag) => tag._id !== tagId),
          }),
          false,
          'removeContactTag'
        ),
      addOrRemoveContactTag: (newTag) =>
        set(
          ({ tags }) => {
            if (tags.some((tag) => tag._id === newTag._id)) {
              return {
                tags: tags.filter((tag) => tag._id !== newTag._id),
              };
            } else {
              return {
                tags: [...tags, newTag],
              };
            }
          },
          false,
          'addOrRemoveContactTag'
        ),
      setLoadFbPastMessages: (value) =>
        set({ load_fb_past_messages: value }, false, 'setLoadFbPastMessages'),

      setPage: (page) => set({ page }, false, 'setPage'),
      setAINotes: (ai_notes) => set({ ai_notes }, false, 'setAINotes'),
      setMessages: (messages, responseType = null) =>
        set({ messages, responseType }, false, 'setMessages'),
      setNewMessage: (newMessage) =>
        set(
          (state) => {
            // Prevent duplicate messages when API response arrives after websocket message
            const messageExists = state.messages.some(
              (msg) => msg._id === newMessage._id
            );
            if (messageExists) {
              return state; // Message already exists, don't add duplicate
            }
            return { messages: [...state.messages, newMessage] };
          },
          false,
          'setNewMessage'
        ),
      addMessageToEmailThread: (threadId, newMessage) =>
        set(
          (state) => {
            // Find the thread and append the message to it
            const updatedMessages = state.messages.map((thread) => {
              if (thread.thread_id === threadId) {
                // Defensive check: ensure thread.messages is an array
                const existingMessages = Array.isArray(thread.messages)
                  ? thread.messages
                  : [];

                // Check if message already exists in thread
                const messageExists = existingMessages.some(
                  (msg) => msg._id === newMessage._id
                );
                if (messageExists) {
                  return thread; // Message already exists, don't add duplicate
                }

                return {
                  ...thread,
                  messages: [...existingMessages, newMessage],
                  message_count: (thread.message_count || 0) + 1,
                  latest_timestamp: newMessage.timestamp,
                  latest_direction: newMessage.direction,
                  content: newMessage.content,
                  content_text: newMessage.content_text,
                };
              }
              return thread;
            });
            return { messages: updatedMessages };
          },
          false,
          'addMessageToEmailThread'
        ),
      setPrevPageMessages: (prevMessages, responseType = null) =>
        set(
          (state) => {
            const effectiveResponseType = responseType || state.responseType;

            // Handle merged channel mixed content (threads + regular messages)
            if (isMergedChannel(effectiveResponseType)) {
              // For mixed content: use thread_id for threads, _id for regular messages
              const existingIds = new Set();
              state.messages.forEach((item) => {
                if (item.thread_id) {
                  existingIds.add(`thread_${item.thread_id}`);
                } else if (item._id) {
                  existingIds.add(`msg_${item._id}`);
                }
              });

              const uniquePrevItems = prevMessages.filter((item) => {
                if (item.thread_id) {
                  return !existingIds.has(`thread_${item.thread_id}`);
                } else if (item._id) {
                  return !existingIds.has(`msg_${item._id}`);
                }
                return true;
              });

              return {
                messages: [...uniquePrevItems, ...state.messages],
                responseType: effectiveResponseType,
              };
            }

            // Auto-detect if we're dealing with threads by checking for thread_id property
            const isThreads =
              prevMessages.length > 0 && prevMessages[0]?.thread_id;

            if (isThreads) {
              // For email threads, check thread_id instead of _id
              const existingThreadIds = new Set(
                state.messages.map((thread) => thread.thread_id).filter(Boolean)
              );
              const uniquePrevThreads = prevMessages.filter(
                (thread) => !existingThreadIds.has(thread.thread_id)
              );
              return {
                messages: [...uniquePrevThreads, ...state.messages],
                responseType: effectiveResponseType,
              };
            } else {
              // Prevent duplicates when prepending paginated messages
              const existingIds = new Set(
                state.messages.map((msg) => msg._id).filter(Boolean)
              );
              const uniquePrevMessages = prevMessages.filter(
                (msg) => !existingIds.has(msg._id)
              );
              return {
                messages: [...uniquePrevMessages, ...state.messages],
                responseType: effectiveResponseType,
              };
            }
          },
          false,
          'setPrevPageMessages'
        ),
      setMessageReaction: (messageId, emoji) =>
        set(
          (state) => ({
            messages: state.messages.map((message) =>
              message._id === messageId ? { ...message, emoji } : message
            ),
          }),
          false,
          'set_message_reaction'
        ),
      setMessageCustomerReaction: (messageId, customerEmoji) =>
        set(
          (state) => ({
            messages: state.messages.map((message) =>
              message._id === messageId
                ? {
                    ...message,
                    customer_reaction:
                      customerEmoji === 'love' ? '❤' : customerEmoji,
                  }
                : message
            ),
          }),
          false,
          'setMessageCustomerReaction'
        ),
      setMessageReadReceipt: (messageId, isSeenTimestamp) =>
        set(
          (state) => ({
            messages: state.messages.map((message) =>
              message._id === messageId
                ? { ...message, is_seen: true, is_seen_at: isSeenTimestamp }
                : message
            ),
          }),
          false,
          'setMessageReadReceipt'
        ),

      resetContactStore: () =>
        set({ ...initialState }, false, 'resetContactStore'),

      setParentCommentId: (parentCommentId) =>
        set({ parentCommentId }, false, 'setParentCommentId'),
      setCommentThread: (commentThread) =>
        set({ commentThread }, false, 'setCommentThread'),
      setCommentThreadAccordianState: (accordianState) =>
        set(
          {
            commentThreadAccordianState: accordianState,
          },
          false,
          'setCommentThreadAccordianState'
        ),
      setCommentMessage: (message) =>
        set({ commentMessage: message }, false, 'setCommentMessage'),
      resetCommentThread: () =>
        set(
          {
            commentThread: [],
            parentCommentId: null,
            commentThreadAccordianState: '',
            commentMessage: '',
          },
          false,
          'resetCommentThread'
        ),
      setSelectedPrivateReplyMessage: (messageId, messageText) =>
        set(
          {
            selectedPrivateReplyMessage: messageText,
            selectedPrivateReplyMessageId: messageId,
          },
          false,
          'setSelectedPrivateReplyMessage'
        ),
      resetSelectedPrivateReplyMessage: () =>
        set(
          {
            selectedPrivateReplyMessage: '',
            selectedPrivateReplyMessageId: null,
          },
          false,
          'resetSelectedPrivateReplyMessage'
        ),
      setSelectedEmailThreadForReply: (threadId, subject, preview) =>
        set(
          {
            selectedEmailThreadForReply: {
              thread_id: threadId,
              subject,
              preview,
            },
          },
          false,
          'setSelectedEmailThreadForReply'
        ),
      resetSelectedEmailThreadForReply: () =>
        set(
          {
            selectedEmailThreadForReply: null,
          },
          false,
          'resetSelectedEmailThreadForReply'
        ),
      setSelectedReplyToMessage: (message) =>
        set(
          {
            selectedReplyToMessage: message
              ? {
                  _id: message._id,
                  text: message.text || '',
                  message_type: message.message_type,
                  media_url: message.media_url || null,
                }
              : null,
          },
          false,
          'setSelectedReplyToMessage'
        ),
      resetSelectedReplyToMessage: () =>
        set(
          {
            selectedReplyToMessage: null,
          },
          false,
          'resetSelectedReplyToMessage'
        ),
      updateCommentResponded: (messageId) =>
        set(
          (state) => ({
            messages: state.messages.map((message) =>
              message._id === messageId
                ? { ...message, is_privately_responded: true }
                : message
            ),
          }),
          false,
          'updateCommentResponded'
        ),
      addCopilotMessage: (message) =>
        set(
          (state) => ({
            copilotMessages: [...state.copilotMessages, message],
          }),
          false,
          'addCopilotMessage'
        ),
      resetCopilotMessages: () =>
        set({ copilotMessages: [] }, false, 'resetCopilotMessages'),
      updateCopilotMessageFeedback: (messageId, responseId, isUseful) =>
        set(
          (state) => ({
            copilotMessages: state.copilotMessages.map((message) =>
              message._id === messageId && message.response_id === responseId
                ? {
                    ...message,
                    feedback: {
                      is_useful: isUseful,
                    },
                  }
                : message
            ),
          }),
          false,
          'updateCopilotMessageFeedback'
        ),
      setMessageToTweak: (message) =>
        set({ messageToTweak: message }, false, 'setMessageToTweak'),
      resetMessageToTweak: () =>
        set({ messageToTweak: '' }, false, 'resetMessageToTweak'),
      setCopilotOpen: (isOpen) =>
        set({ isCopilotOpen: isOpen }, false, 'setCopilotOpen'),
      setCopilotLoading: (isLoading) =>
        set({ isCopilotLoading: isLoading }, false, 'setCopilotLoading'),
      setAudioMessageTranscription: (messageId, status = 'queued') =>
        set(
          (state) => ({
            messages: state.messages.map((message) =>
              message._id === messageId
                ? { ...message, transcribe: { status: status } }
                : message
            ),
          }),
          false,
          'setAudioMessageTranscription'
        ),
      setIsShowingMobileProfile: (isShowing) =>
        set(
          { isShowingMobileProfile: isShowing },
          false,
          'setIsShowingMobileProfile'
        ),
      setAudioMessageTranscriptionText: (messageId, text) =>
        set(
          (state) => ({
            messages: state.messages.map((message) =>
              message._id === messageId
                ? {
                    ...message,
                    transcribe: { status: 'done', text: text },
                  }
                : message
            ),
          }),
          false,
          'setAudioMessageTranscriptionText'
        ),
      setMessageDelete: (messageId) =>
        set(
          (state) => ({
            messages: state.messages.map((message) =>
              message._id === messageId
                ? { ...message, is_deleted: true }
                : message
            ),
          }),
          false,
          'setMessageDelete'
        ),
    }),

    { name: 'ContactStore' }
  )
);

export default useContactStore;
